/**
 * shared/lib/security-posture/tier-ladder/moves-guard.ts
 *
 * `isLadderMoves` — the runtime narrowing `isLadderReading` (`./reading`)
 * calls when a wire payload's `moves` field is present, so a
 * present-but-malformed value fails the WHOLE reading closed (AC-1, spec
 * `2026-09-04-security-ladder-board` module 02 §1) rather than rendering a
 * board with a corrupt row. Split out of `./moves` (300-line modularity
 * rule) — types live in `./moves-types`, the pure derivation in `./moves`;
 * this file is validation only and feeds nothing back into either.
 *
 * STRUCTURAL, NOT SEMANTIC: every enum here is checked against its closed
 * set, every count against `Number.isFinite`, every array against its
 * element shape. This does NOT re-derive whether a value is CORRECT (e.g.
 * that `reaches` matches `STAR_WEIGHT` for its move) — only that the shape a
 * client could safely render actually crossed the wire. Fix round 1, finding
 * 3: `byKind` is checked against the closed FIVE-axis vocabulary
 * (`ATOM_AXIS_IDS`), not merely "whatever keys are present are finite" — an
 * empty `{}` used to validate, silently accepting a payload missing every
 * axis `MemorySetSummary` documents as always-present.
 */

import { ATOM_AXIS_IDS } from "../../bespoke-seed/atom-axes";
import { HARDWARE_ROUTES } from "../hardware-route";
import {
  MEMORY_LADDER_STATES,
  RECOVERY_NET_STATES,
  type LadderMoves,
  type MoveState,
  type Reaches,
  type RecoveryNetBlocker,
} from "./moves-types";

/**
 * Every closed set below is BUILT FROM the tuple its type is derived from
 * (final fix round, code-review Important 3) — `HARDWARE_ROUTES`
 * (`../hardware-route`), `MEMORY_LADDER_STATES`/`RECOVERY_NET_STATES`
 * (`./moves-types`). Before this the three were restated here as bare
 * `Set<unknown>` literals, so adding a value to any of those unions compiled
 * clean everywhere and then failed validation silently at this boundary.
 * `MOVE_STATES`/`REACHES`/`RECOVERY_NET_BLOCKERS` stay literal but are typed
 * `Set<T>`, which makes a TYPO a compile error (a MISSING member is what the
 * three tuples above are for).
 */
const MOVE_STATES: ReadonlySet<unknown> = new Set<MoveState>(["available", "waiting", "done"]);
const REACHES: ReadonlySet<unknown> = new Set<Reaches>(["alone", "with_another"]);
const MEMORY_LADDER_STATE_SET: ReadonlySet<unknown> = new Set(MEMORY_LADDER_STATES);
const RECOVERY_NET_STATE_SET: ReadonlySet<unknown> = new Set(RECOVERY_NET_STATES);
const HARDWARE_ROUTE_SET: ReadonlySet<unknown> = new Set(HARDWARE_ROUTES);
const RECOVERY_NET_BLOCKERS: ReadonlySet<unknown> = new Set<RecoveryNetBlocker>([
  "guardian_not_yet_accepted", "guardian_still_new", "review_due", "no_trusted_people", "needs_more",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

function isFiniteCount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isMaturesAtMs(value: unknown): value is number | null {
  return value === null || (typeof value === "number" && Number.isFinite(value));
}

/** AC-31 — `focusCredentialId` is OPTIONAL (absent narrows fine, an older
 *  reading never carried it) and, when present, either a `string` or an
 *  explicit `null`; anything else (a number, an object, an array) fails
 *  closed, the same structural posture every other field here already
 *  gets. */
function isFocusCredentialId(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === "string";
}

function isSecondPasskey(value: unknown): value is LadderMoves["secondPasskey"] {
  if (!isRecord(value)) return false;
  return MOVE_STATES.has(value.state) && REACHES.has(value.reaches)
    && isFiniteCount(value.activeCredentials) && isMaturesAtMs(value.maturesAtMs)
    && isFocusCredentialId(value.focusCredentialId);
}

function isHardwareKey(value: unknown): value is LadderMoves["hardwareKey"] {
  if (!isRecord(value)) return false;
  return HARDWARE_ROUTE_SET.has(value.route) && MOVE_STATES.has(value.state) && REACHES.has(value.reaches)
    && isMaturesAtMs(value.maturesAtMs) && isFocusCredentialId(value.focusCredentialId);
}

function isByKind(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return ATOM_AXIS_IDS.every((axisId) => isFiniteCount(value[axisId]));
}

function isMemories(value: unknown): value is LadderMoves["memories"] {
  if (!isRecord(value)) return false;
  return MEMORY_LADDER_STATE_SET.has(value.ladder) && REACHES.has(value.reaches)
    && isFiniteCount(value.saved) && isFiniteCount(value.confirmed) && isFiniteCount(value.kindsCovered)
    && isByKind(value.byKind);
}

function isGuardianRow(value: unknown): boolean {
  return isRecord(value) && typeof value.guardianId === "string" && typeof value.label === "string"
    && typeof value.questionsSet === "boolean";
}

function isGuardianCheck(value: unknown): value is NonNullable<LadderMoves["guardianCheck"]> {
  if (!isRecord(value)) return false;
  return REACHES.has(value.reaches) && Array.isArray(value.guardians) && value.guardians.every(isGuardianRow)
    && typeof value.done === "boolean";
}

function isRecoveryNet(value: unknown): value is NonNullable<LadderMoves["recoveryNet"]> {
  if (!isRecord(value)) return false;
  return RECOVERY_NET_STATE_SET.has(value.state) && Array.isArray(value.blockers)
    && value.blockers.every((blocker) => RECOVERY_NET_BLOCKERS.has(blocker));
}

/**
 * Narrows an untrusted `moves` value. Absence is the CALLER's concern
 * (`isLadderReading` treats a missing `moves` as valid) — this only judges a
 * value that IS present, and rejects fail-closed on anything else.
 */
export function isLadderMoves(value: unknown): value is LadderMoves {
  if (!isRecord(value)) return false;
  if (!isSecondPasskey(value.secondPasskey)) return false;
  if (!isHardwareKey(value.hardwareKey)) return false;
  if (!isMemories(value.memories)) return false;
  if (value.guardianCheck !== undefined && !isGuardianCheck(value.guardianCheck)) return false;
  if (value.recoveryNet !== undefined && !isRecoveryNet(value.recoveryNet)) return false;
  return true;
}
